import React from 'react';
import { AppShell } from '@/components';

const VSCodeExtensionPage: React.FC = () => {
  return (
    <AppShell>
      <div className="bg-slate-950 text-slate-50">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-b from-slate-900 via-slate-950 to-slate-950">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid gap-12 lg:grid-cols-2 items-center">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <svg className="w-12 h-12" viewBox="0 0 100 100" fill="none" aria-hidden="true">
                    <rect width="100" height="100" rx="20" fill="#007ACC" />
                    <path d="M75 25L50 75L25 25" stroke="white" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span className="text-sm bg-emerald-500/15 text-emerald-300 px-3 py-1 rounded-full font-medium">
                    Available on the Visual Studio Code Marketplace
                  </span>
                </div>

                <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
                  Deploy functions from VS Code, governed by Relay
                </h1>
                <p className="mt-6 text-lg leading-8 text-slate-300 max-w-2xl">
                  The Relay VS Code extension brings function IDs, execution history, and policy automation into your editor. Ship faster without leaving your development flow.
                </p>
                <div className="mt-10 flex flex-wrap items-center gap-4">
                  <a
                    href="https://marketplace.visualstudio.com/items?itemName=relay.control-plane"
                    className="inline-flex items-center justify-center rounded-lg bg-emerald-500 px-6 py-3 text-base font-semibold text-slate-950 shadow-lg shadow-emerald-500/30 transition hover:bg-emerald-400"
                  >
                    Install extension
                  </a>
                  <a
                    href="/docs"
                    className="inline-flex items-center justify-center rounded-lg border border-white/20 px-6 py-3 text-base font-semibold text-slate-200 transition hover:border-white hover:text-white"
                  >
                    Read the guide
                  </a>
                </div>
              </div>

              <div className="rounded-2xl border border-white/10 bg-white/5 p-6 shadow-2xl shadow-emerald-500/10">
                <h2 className="text-lg font-semibold text-white">Highlights</h2>
                <ul className="mt-4 space-y-4 text-sm leading-6 text-slate-200">
                  <li><span className="text-emerald-400">●</span> Publish and promote functions with one command palette action.</li>
                  <li><span className="text-emerald-400">●</span> View execution history, logs, and policy status inline.</li>
                  <li><span className="text-emerald-400">●</span> Generate short function IDs and copy auth-ready snippets for teammates.</li>
                  <li><span className="text-emerald-400">●</span> Trigger workflows and rollbacks using Relay’s CLI under the hood.</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Feature Grid */}
        <section className="py-20">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid gap-8 md:grid-cols-2">
              {[{
                title: 'Function command palette',
                description: 'Search functions by ID, name, or tags. Promote branches, view policy context, and turn on monetization once tests pass.'
              }, {
                title: 'Inline execution history',
                description: 'Inspect the last 20 runs per function — status, latency, cost, and runbook links — without opening a browser.'
              }, {
                title: 'Namespace-aware controls',
                description: 'Switch tenants or environments while Relay enforces namespace policy, secrets, and approvals automatically.'
              }, {
                title: 'Ready-to-share snippets',
                description: 'Copy authenticated fetch snippets pre-filled with function ID, environment, and pricing tier for product teams.'
              }].map((item) => (
                <div key={item.title} className="rounded-2xl border border-white/10 bg-white/5 p-6 shadow-lg shadow-black/20">
                  <h3 className="text-lg font-semibold text-white">{item.title}</h3>
                  <p className="mt-3 text-sm leading-6 text-slate-300">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Call to action */}
        <section className="py-20 bg-slate-900">
          <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-semibold text-white sm:text-4xl">Bring Relay’s control plane into your editor</h2>
            <p className="mt-4 text-base leading-7 text-slate-300">Install the VS Code extension, connect your workspace, and keep policy, telemetry, and monetization in sight while you code.</p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <a
                href="https://marketplace.visualstudio.com/items?itemName=relay.control-plane"
                className="inline-flex items-center justify-center rounded-lg bg-emerald-500 px-6 py-3 text-base font-semibold text-slate-950 transition hover:bg-emerald-400"
              >
                Install from Marketplace
              </a>
              <a
                href="/contact"
                className="inline-flex items-center justify-center rounded-lg border border-white/20 px-6 py-3 text-base font-semibold text-slate-200 transition hover:border-white hover:text-white"
              >
                Talk to product team
              </a>
            </div>
          </div>
        </section>
      </div>
    </AppShell>
  );
};

export default VSCodeExtensionPage;
